import type { FC, ErrorInfo, ReactNode } from "react";
import { Component } from "react";
import { handleGlobalError } from "../lib/errorLogging";
import { hideGlobalLoading } from "./GlobalLoadingIndicator";

interface Props {
  children: ReactNode;
  fallback?: FC<{ error: Error }>;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const DefaultFallback: FC<{ error: Error }> = ({ error }) => {
  return (
    <div className="flex h-screen flex-col items-center justify-center p-4">
      <div className="rounded-lg bg-white p-8 shadow-xl dark:bg-gray-800">
        <h1 className="mb-4 text-2xl font-bold text-red-600 dark:text-red-500">
          Something went wrong
        </h1>
        <div className="mb-4 text-gray-600 dark:text-gray-300">
          {error.message}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          Reload Page
        </button>
      </div>
    </div>
  );
};

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    hideGlobalLoading(); // Ensure loading indicator is hidden on error
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    hideGlobalLoading(); // Ensure loading indicator is hidden
    handleGlobalError(error, errorInfo); // Log error with our error logging system
  }

  public render() {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback || DefaultFallback;
      return <FallbackComponent error={this.state.error} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
